import React, { createContext, useContext, useState, useEffect } from 'react';
import { productAPI } from '../services/api';
import { useCategories } from './CategoryContext';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const { categories: CATEGORIES } = useCategories();
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [filters, setFilters] = useState({
    category: '',
    subcategory: '',
    brand: '',
    origin: '',
    priceRange: { min: 0, max: 100 },
    rating: 0,
    inStock: false,
    isNew: false,
    isPopular: false,
    isDiscount: false,
    allergens: []
  });

  // Load all products on component mount
  useEffect(() => {
    loadAllProducts();
  }, []);

  const loadAllProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productAPI.getAllProducts();
      setAllProducts(response.data || []);
      setSearchResults(response.data || []);
    } catch (err) {
      console.error('Ürünler yüklenirken hata oluştu:', err);
      setError('Ürünler yüklenirken bir hata oluştu');
      setAllProducts([]);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (term, appliedFilters = filters) => {
    try {
      setLoading(true);
      setError(null);
      setSearchQuery(term);
      
      let results = [...allProducts];

      // Apply search term
      if (term && term.trim()) {
        const response = await productAPI.searchProducts(term);
        results = response.data || [];
      }
      
      // Apply filters
      if (appliedFilters.category) {
        results = results.filter(p => p.category === appliedFilters.category);
      }
      
      if (appliedFilters.subcategory) {
        results = results.filter(p => p.subcategory === appliedFilters.subcategory);
      }
      
      if (appliedFilters.brand) {
        results = results.filter(p => p.brand === appliedFilters.brand);
      }
      
      if (appliedFilters.origin) {
        results = results.filter(p => p.origin === appliedFilters.origin);
      }
      
      if (appliedFilters.priceRange && appliedFilters.priceRange.min !== undefined) {
        results = results.filter(p => p.price >= appliedFilters.priceRange.min);
      }
      
      if (appliedFilters.priceRange && appliedFilters.priceRange.max !== undefined) {
        results = results.filter(p => p.price <= appliedFilters.priceRange.max);
      }
      
      if (appliedFilters.rating) {
        results = results.filter(p => p.rating >= parseFloat(appliedFilters.rating));
      }
      
      if (appliedFilters.inStock) {
        results = results.filter(p => p.inStock === true);
      }
      
      if (appliedFilters.isNew) {
        results = results.filter(p => p.newProduct === true);
      }

      if (appliedFilters.isPopular) {
        results = results.filter(p => p.featured === true);
      }

      if (appliedFilters.isDiscount) {
        results = results.filter(p => p.onSale === true);
      }
      
      if (appliedFilters.allergens && appliedFilters.allergens.length > 0) {
        results = results.filter(p => 
          !appliedFilters.allergens.some(allergen => 
            p.allergens && p.allergens.includes(allergen)
          )
        );
      }

      // Apply sorting
      if (sortBy === 'price-low') {
        results.sort((a, b) => a.price - b.price);
      } else if (sortBy === 'price-high') {
        results.sort((a, b) => b.price - a.price);
      } else if (sortBy === 'rating') {
        results.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      } else if (sortBy === 'newest') {
        results.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      } else if (sortBy === 'name') {
        results.sort((a, b) => a.name.localeCompare(b.name));
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Arama yapılırken hata oluştu:', err);
      setError('Arama yapılırken bir hata oluştu');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = (newFilters) => {
    try {
      setLoading(true);
      setError(null);
      setFilters(newFilters);
      
      let results = [...allProducts];

      // Apply filters
      if (newFilters.category) {
        results = results.filter(p => p.category === newFilters.category);
      }
      
      if (newFilters.subcategory) {
        results = results.filter(p => p.subcategory === newFilters.subcategory);
      }
      
      if (newFilters.brand) {
        results = results.filter(p => p.brand === newFilters.brand);
      }
      
      if (newFilters.origin) {
        results = results.filter(p => p.origin === newFilters.origin);
      }
      
      if (newFilters.priceRange && newFilters.priceRange.min !== undefined) {
        results = results.filter(p => p.price >= newFilters.priceRange.min);
      }
      
      if (newFilters.priceRange && newFilters.priceRange.max !== undefined) {
        results = results.filter(p => p.price <= newFilters.priceRange.max);
      }
      
      if (newFilters.rating) {
        results = results.filter(p => p.rating >= parseFloat(newFilters.rating));
      }
      
      if (newFilters.inStock) {
        results = results.filter(p => p.inStock === true);
      }
      
      if (newFilters.isNew) {
        results = results.filter(p => p.newProduct === true);
      }

      if (newFilters.isPopular) {
        results = results.filter(p => p.featured === true);
      }

      if (newFilters.isDiscount) {
        results = results.filter(p => p.onSale === true);
      }
      
      if (newFilters.allergens && newFilters.allergens.length > 0) {
        results = results.filter(p => 
          !newFilters.allergens.some(allergen => 
            p.allergens && p.allergens.includes(allergen)
          )
        );
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Filtreleme yapılırken hata oluştu:', err);
      setError('Filtreleme yapılırken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const updateFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const resetFilters = () => {
    setFilters({
      category: '',
      subcategory: '',
      brand: '',
      origin: '',
      priceRange: { min: 0, max: 100 },
      rating: 0,
      inStock: false,
      isNew: false,
      isPopular: false,
      isDiscount: false,
      allergens: []
    });
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      subcategory: '',
      brand: '',
      origin: '',
      priceRange: { min: 0, max: 100 },
      rating: 0,
      inStock: false,
      isNew: false,
      isPopular: false,
      isDiscount: false,
      allergens: []
    });
    setSearchResults(allProducts);
  };

  const getFilterOptions = () => {
    // Basic unique values from products
    const dynamicCategories = [...new Set(allProducts.map(p => p.category))];
    const dynamicSubcategories = [...new Set(allProducts.map(p => p.subcategory))];
    const dynamicBrands = [...new Set(allProducts.map(p => p.brand))];
    const dynamicOrigins = [...new Set(allProducts.map(p => p.origin))];
    const dynamicAllergens = [...new Set(allProducts.flatMap(p => p.allergens || []))];

    // Merge with predefined categories
    const categories = [...new Set([...CATEGORIES.map(c => c.id), ...dynamicCategories])];
    
    // Get subcategories for current active category
    let subcategories = [];
    if (filters.category) {
      const predefined = CATEGORIES.find(c => c.id === filters.category)?.subcategories?.map(s => s.id) || [];
      const dynamic = [...new Set(allProducts.filter(p => p.category === filters.category).map(p => p.subcategory).filter(Boolean))];
      subcategories = [...new Set([...predefined, ...dynamic])];
    } else {
      subcategories = dynamicSubcategories.filter(Boolean);
    }

    return {
      categories: categories.filter(Boolean),
      subcategories: subcategories.filter(Boolean),
      brands: dynamicBrands.filter(Boolean),
      origins: dynamicOrigins.filter(Boolean),
      allergens: dynamicAllergens.filter(Boolean)
    };
  };

  const getProductById = (id) => {
    return allProducts.find(p => p.id === parseInt(id));
  };

  const getProductsByCategory = (category) => {
    return allProducts.filter(p => p.category === category);
  };

  const getFeaturedProducts = () => {
    return allProducts.filter(p => p.featured);
  };

  const getPopularProducts = () => {
    return allProducts.filter(p => p.featured);
  };

  const getNewProducts = () => {
    return allProducts.filter(p => p.newProduct);
  };

  const getOnSaleProducts = () => {
    return allProducts.filter(p => p.onSale);
  };

  const value = {
    searchResults,
    allProducts,
    loading,
    isSearching: loading,
    error,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    filters,
    updateFilters,
    resetFilters,
    searchProducts,
    filterProducts,
    clearFilters,
    getFilterOptions,
    getProductById,
    getProductsByCategory,
    getFeaturedProducts,
    getPopularProducts,
    getNewProducts,
    getOnSaleProducts
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export default SearchContext;